export const caseStudies = [
  {
    id: 1,
    title: "AI-Powered Customer Support Chatbot",
    category: "AI & Machine Learning",
    problem: "A fast-growing eCommerce retailer struggled with high support volume and slow response times. Customers had to wait days for answers, and support costs were escalating.",
    solution: "We developed an AI-driven chatbot using Python and OpenAI’s GPT-4 via LangChain, integrated into the client’s Node.js/Express backend. We built a RAG (Retrieval-Augmented Generation) system: customer queries are augmented with real-time product and policy data fetched from a vector database (e.g. Pinecone) before invoking the LLM.",
    results: [
      "70% of routine queries handled automatically",
      "80% drop in response time",
      "25% rise in CSAT scores",
      "60% savings on support staff time"
    ],
    technologies: ["Python", "LangChain", "OpenAI API", "Node.js", "PostgreSQL", "Pinecone", "WhatsApp API"],
    image: "/images/ai-chatbot.png"
  },
  {
    id: 2,
    title: "Scalable SaaS Platform (Microservices Architecture)",
    category: "SaaS & Microservices",
    problem: "A startup’s monolithic web app could not scale for a rapidly growing user base. New feature releases were slow, and outages occurred when one component failed.",
    solution: "We re-architected the product into microservices. Each service (auth, billing, notifications) runs in its own Node.js application and communicates via RabbitMQ and REST/gRPC APIs. The system is containerized with Docker and orchestrated using Kubernetes (EKS on AWS).",
    results: [
      "Handled 5× user increase without downtime",
      "Increased deployment frequency to weekly releases",
      "Fault isolation: systems remain operational if one service fails",
      "Improved scalability and flexibility"
    ],
    technologies: ["Node.js", "Docker", "Kubernetes", "RabbitMQ", "PostgreSQL", "Redis", "AWS EKS", "Terraform"],
    image: "/images/saas-platform.png"
  },
  {
    id: 3,
    title: "Headless eCommerce Platform",
    category: "eCommerce",
    problem: "A retail client’s legacy online store (PHP monolith) was slow and hard to update. They needed a modern, responsive platform with real-time analytics.",
    solution: "We built a headless commerce solution. The front end is a React/Next.js web app for fast page loads, consuming product data via APIs from a Strapi headless CMS. We implemented SSG for key catalog pages for instant delivery over a CDN.",
    results: [
      "50% drop in page load times",
      "Improved search rankings (SEO)",
      "30% fall in cart abandonment",
      "Faster developer updates via CI/CD"
    ],
    technologies: ["React", "Next.js", "Strapi", "MongoDB", "GraphQL", "Stripe API", "AWS CloudFront"],
    image: "/images/ecommerce-platform.png"
  },
  {
    id: 4,
    title: "Cross-Platform Mobile App (iOS/Android)",
    category: "Mobile Development",
    problem: "A service marketplace client needed mobile apps for iOS and Android to reach customers on the go, wanting to reduce dev time by sharing code.",
    solution: "We chose Flutter to build a single codebase for both platforms. Using Dart, we created a responsive app with polished UI and integrated native features (camera, GPS, push notifications) via plugins.",
    results: [
      "Delivered in half the time of separate native builds",
      "4.8★ average app store rating",
      "40% increase in user engagement",
      "Improved customer retention"
    ],
    technologies: ["Flutter", "Dart", "Node.js", "PostgreSQL", "Firebase", "Google Maps API"],
    image: "/images/mobile-app.png"
  },
  {
    id: 5,
    title: "Cloud Migration & DevOps Modernization",
    category: "Cloud & DevOps",
    problem: "A mid-sized enterprise ran on-premise servers and faced downtime during peak loads, lacking automated deployments and environment consistency.",
    solution: "We led a full cloud migration to AWS and instituted a DevOps pipeline. Applications were containerized (Docker) and hosted on AWS Fargate/ECS. Infrastructure-as-code (Terraform) automated the setup.",
    results: [
      "3× improvement in response times under load",
      "Zero outage incidents post-migration",
      "Release cycles shortened from months to days",
      "Cost savings through elastic scaling"
    ],
    technologies: ["AWS (ECS/Fargate)", "Docker", "Terraform", "GitHub Actions", "Datadog", "CloudWatch"],
    image: "/images/cloud-migration.png"
  },
  {
    id: 6,
    title: "Data Analytics & BI Transformation",
    category: "Data & Analytics",
    problem: "A retail chain had data trapped in disparate systems with no unified reporting, making it hard to get actionable insights.",
    solution: "We built a data warehouse and analytics platform. Data is ingested into AWS S3, processed via AWS Glue/Spark, and loaded into Amazon Redshift. A BI layer (Looker/Power BI) provides interactive dashboards.",
    results: [
      "Real-time insights for store managers",
      "20% reduction in stockouts",
      "30% improvement in forecast accuracy",
      "Data-driven strategic decisions"
    ],
    technologies: ["Python", "PySpark", "AWS Glue", "Amazon Redshift", "Looker", "Airflow"],
    image: "/images/data-analytics.png"
  },
  {
    id: 7,
    title: "Enterprise Cybersecurity Overhaul",
    category: "Cybersecurity",
    problem: "A fintech client needed to secure its web applications and APIs against threats after experiencing minor breaches due to outdated libraries.",
    solution: "We instituted a DevSecOps approach guided by OWASP Top 10. Enforced input validation, moved secrets to AWS Secrets Manager, and implemented MFA and RBAC using OAuth2/JWT.",
    results: [
      "Zero critical vulnerabilities remaining",
      "0 new OWASP-identified issues",
      "Increased customer trust and compliance",
      "Hardened microservice security"
    ],
    technologies: ["OWASP", "AWS IAM", "OAuth2/JWT", "AWS WAF", "Snyk", "SonarQube", "Splunk"],
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2000&auto=format&fit=crop"
  },
  {
    id: 8,
    title: "IoT Edge Solution for Smart Monitoring",
    category: "IoT & Edge",
    problem: "An industrial client needed real-time equipment monitoring with minimal latency and functionality during intermittent connectivity.",
    solution: "We deployed an edge computing architecture using AWS IoT Greengrass. Sensors feed data to local Raspberry Pi gateways running Python for local preprocessing and anomaly detection.",
    results: [
      "Real-time alerts with <1s latency",
      "Resilient operation during network loss",
      "50% increase in maintenance uptime",
      "Seamless cloud synchronization"
    ],
    technologies: ["AWS IoT Greengrass", "MQTT", "Python", "AWS Lambda", "DynamoDB", "Grafana"],
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2000&auto=format&fit=crop"
  },
  {
    id: 9,
    title: "Mobile Banking App UI/UX Overhaul",
    category: "UI/UX Design",
    problem: "A mobile banking app suffered from a confusing interface: customers struggled with navigation, the sign-up funnel dropped off, and usability ratings were low.",
    solution: "We applied a user-centered design process. First, we conducted usability tests and collected feedback. We re-designed the app in Figma, focusing on a streamlined user flow, simplified dashboard, and progressive onboarding.",
    results: [
      "Session duration increased by 35%",
      "Onboarding completion jumped from 45% to 80%",
      "App store ratings improved to 4.7 stars",
      "Support calls declined by 50%"
    ],
    technologies: ["React Native", "Figma", "Styled Components", "Accessibility", "TypeScript"],
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2000&auto=format&fit=crop"
  },
  {
    id: 10,
    title: "Brand Identity & Graphic Design",
    category: "Graphic Design",
    problem: "A new AI startup had no visual identity; their pitch deck and website felt generic, failing to stand out to investors and clients.",
    solution: "We developed a complete branding package including logo, color palette, typography, and an icon set. We applied this identity to marketing materials, pitch decks, and a new responsive website.",
    results: [
      "Greater brand recognition at conferences",
      "Website time-on-page rose by 50%",
      "Secured funding rounds attributed to professional look",
      "Cohesive visuals across all digital channels"
    ],
    technologies: ["Adobe Illustrator", "Photoshop", "Next.js", "Tailwind CSS", "SVG"],
    image: "https://images.unsplash.com/photo-1558655146-d09347e92766?q=80&w=2000&auto=format&fit=crop"
  },
  {
    id: 11,
    title: "SEO & Content Strategy Transformation",
    category: "Digital Marketing",
    problem: "An online health supplements retailer had flat growth, low organic traffic, and was buried on search results by competitors.",
    solution: "We executed an SEO and content marketing strategy including technical fixes, site speed improvements, keyword research, and a comprehensive educational content calendar.",
    results: [
      "Organic search traffic grew by 180%",
      "Top-10 ranking keywords grew 6×",
      "Monthly revenue from SEO sales doubled",
      "Bounce rate improved by 25%"
    ],
    technologies: ["SEO", "Shopify", "Ahrefs", "Content Strategy", "Google Analytics", "Mailchimp"],
    image: "https://images.unsplash.com/photo-1434626881859-194d67b2b86f?q=80&w=2000"
  },
  {
    id: 12,
    title: "PPC & Social Campaign Lead Generation",
    category: "Digital Marketing",
    problem: "A B2B manufacturing firm struggled to generate qualified leads online and had no paid marketing setup to reach decision-makers.",
    solution: "We launched targeted PPC and LinkedIn Ads campaigns with compelling ad creatives and high-converting landing pages, integrated with HubSpot CRM for automatic lead capture.",
    results: [
      "Website leads increased by 300%",
      "Cost per lead dropped by 40%",
      "Qualified demo requests grew by 50%",
      "Overall ad spend ROI reached 4:1"
    ],
    technologies: ["Google Ads", "LinkedIn Ads", "WordPress", "A/B Testing", "HubSpot CRM"],
    image: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?q=80&w=2000&auto=format&fit=crop"
  },
  {
    id: 13,
    title: "n8n Automation Workflow",
    category: "No-Code Automation",
    problem: "An HR SaaS startup manually synced user data across CRM, email, and database systems, leading to errors, delays, and high operational costs.",
    solution: "We implemented n8n, an open-source visual workflow automation platform, to sync signups, welcome emails, and database records autonomously in real-time.",
    results: [
      "Eliminated 80% of manual data entry work",
      "Onboarding time shrank from days to minutes",
      "Staff saved 20 hours per week",
      "Estimated 30% cost saving in operations"
    ],
    technologies: ["n8n", "No-Code", "Docker", "HubSpot", "SendGrid", "PostgreSQL", "Slack API"],
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2000"
  },
  {
    id: 14,
    title: "No-Code Lead Routing Automation",
    category: "No-Code Automation",
    problem: "A property listing portal had leads from multiple channels that agents missed because they were not distributed efficiently in real-time.",
    solution: "We designed a Zapier-based workflow to route and notify leads instantly via email and SMS based on city and property type, with automated CRM entry.",
    results: [
      "Lead response time fell from 24h to <15 min",
      "Lead-to-viewing conversion up by 25%",
      "Agents reported higher lead satisfaction",
      "Manual lead assignment eliminated (10h/week saved)"
    ],
    technologies: ["Zapier", "No-Code", "Twilio", "Airtable", "Gmail API", "Google Chat API"],
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2000&auto=format&fit=crop"
  }
];
